import { cn } from '@/utils/cn';

interface MarpleLogoProps {
  variant?: 'default' | 'white' | 'black' | 'colored';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  className?: string;
  animate?: boolean;
}

const sizeMap = {
  sm: 32,
  md: 48,
  lg: 64,
  xl: 96,
};

const wordmarkSizeMap = {
  sm: 18,
  md: 22,
  lg: 28,
  xl: 40,
};

// 'colored'/'black' render the green brand mark; 'default' is the ink/black
// mark (readable on light backgrounds); 'white' inverts the black mark for
// dark backgrounds — there's no separate white-pixel asset, so we flip the
// black one, same trick the design prototype uses on its hero.
function markSrc(variant: MarpleLogoProps['variant']) {
  return variant === 'colored' ? '/brand/mark-green.png' : '/brand/mark.png';
}
function wordmarkSrc(variant: MarpleLogoProps['variant']) {
  return variant === 'colored' ? '/brand/wordmark-green.png' : '/brand/wordmark.png';
}

export function MarpleLogo({
  variant = 'default',
  size = 'md',
  showText = false,
  className,
  animate = false,
}: MarpleLogoProps) {
  const markHeight = sizeMap[size] ?? sizeMap.sm;
  const wordmarkHeight = wordmarkSizeMap[size] ?? wordmarkSizeMap.sm;
  const invert = variant === 'white';

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <img
        src={markSrc(variant)}
        alt="Marple & Co"
        className={cn('object-contain', animate && 'animate-octagon-spin')}
        style={{ height: markHeight, filter: invert ? 'brightness(0) invert(1)' : undefined }}
      />
      {showText && (
        <img
          src={wordmarkSrc(variant)}
          alt="Marple & Co"
          className="object-contain"
          style={{ height: wordmarkHeight, filter: invert ? 'brightness(0) invert(1)' : undefined }}
        />
      )}
    </div>
  );
}

// Animated version for loading states
export function MarpleLogoAnimated({
  variant = 'default',
  size = 'lg',
}: Omit<MarpleLogoProps, 'animate'>) {
  const markHeight = sizeMap[size] ?? sizeMap.lg;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: markHeight, height: markHeight }}>
      <MarpleLogo variant={variant} size={size} animate />
      <div
        className={cn(
          'absolute inset-0 rounded-full animate-ping opacity-20',
          variant === 'white' ? 'bg-white' : 'bg-[#567D38]'
        )}
      />
    </div>
  );
}

// Logo with product line accent color
export function MarpleLogoWithAccent({
  productLine,
  size = 'md',
  showText = true,
}: {
  productLine: 'cleanser' | 'serum' | 'moisturizer' | 'sunscreen' | 'essence' | 'default';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
}) {
  const accentColors = {
    cleanser: '#42C5E7',
    serum: '#BDD630',
    moisturizer: '#F4C95D',
    sunscreen: '#E07A5F',
    essence: '#1A2E12',
    default: '#567D38',
  };

  const accentColor = accentColors[productLine];

  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <MarpleLogo variant="default" size={size} showText={false} />
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
          style={{ backgroundColor: accentColor }}
        />
      </div>

      {showText && (
        <div className="flex flex-col">
          <span className="font-display font-bold text-xl tracking-tight text-[#1A2E12]">
            MARPLE
          </span>
          <span className="font-elegant italic text-sm" style={{ color: accentColor }}>
            & Co
          </span>
        </div>
      )}
    </div>
  );
}

export default MarpleLogo;
